import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Sparkles, Clock } from "lucide-react";
import { useTerms } from "../lib/BrandContext";
import { shouldShowTrialBanner } from "./trialBannerVisibility";
import EarlyAccessNote from "./EarlyAccessNote";

/**
 * Shows trial status above the coach dashboard.
 * - 0 days remaining (and plan === 'trial'): lock banner + upgrade CTA
 * - 1-3 days remaining: strong amber warning
 * - 4+ days: soft blue info
 * - plan selected (not trial) OR beta_access active: hidden
 */
export default function TrialBanner({ plan }) {
  const { terms } = useTerms();
  if (plan?.early_access) return <EarlyAccessNote />;
  if (!shouldShowTrialBanner(plan)) return null;

  const days = plan.trial_days_remaining ?? 0;
  const expired = plan.trial_expired;

  if (expired) {
    return (
      <div
        data-testid="trial-banner-expired"
        className="rounded-2xl border border-rose-300 bg-gradient-to-br from-rose-50 to-white p-5 shadow-sm"
      >
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-600 text-white">
            <AlertTriangle className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-lg font-bold text-rose-800">Your free trial has ended</p>
            <p className="mt-1 text-sm text-rose-700">
              Choose a paid plan to keep creating fees, sending reminders, and exporting reports.
              Your {terms.teams}, {terms.athletes}, and payment history are safe.
            </p>
            <Link to="/coach/settings#plan" data-testid="trial-banner-upgrade-link">
              <button className="mt-3 inline-flex h-11 items-center rounded-lg bg-rose-600 px-5 text-sm font-semibold text-white hover:bg-rose-700">
                Choose a plan
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (days <= 3) {
    return (
      <div
        data-testid="trial-banner-warning"
        className="rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 to-white p-5 shadow-sm"
      >
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500 text-white">
            <Clock className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-base font-bold text-amber-800">
              Your free trial ends in {days} {days === 1 ? "day" : "days"}
            </p>
            <p className="mt-1 text-sm text-amber-800">
              Choose a paid plan to keep using premium features without interruption.
            </p>
            <Link to="/coach/settings#plan" data-testid="trial-banner-choose-plan">
              <button className="mt-3 inline-flex h-10 items-center rounded-lg bg-amber-600 px-4 text-sm font-semibold text-white hover:bg-amber-700">
                Choose a plan
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="trial-banner-info"
      className="rounded-xl border border-brand-100 bg-gradient-to-br from-brand-50 to-white p-4"
    >
      <div className="flex items-center gap-3">
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand text-white">
          <Sparkles className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink-900">
            Free Trial · <span className="text-brand">{days} {days === 1 ? "day" : "days"} remaining</span>
          </p>
          <p className="text-xs text-ink-500">No credit card required. Choose a plan any time.</p>
        </div>
        <Link to="/coach/settings#plan" className="shrink-0 text-sm font-semibold text-brand hover:underline" data-testid="trial-banner-see-plans">
          See plans →
        </Link>
      </div>
    </div>
  );
}
